import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Search, ChevronRight, ChevronDown, ChevronsUpDown, ChevronsDownUp, Copy, RotateCcw, Save, RefreshCw,
  AlertTriangle, Lock, Users, SearchX, PackageOpen, Info, ShieldCheck, ListOrdered,
} from "lucide-react";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import Tooltip from "components/ui/Tooltip";
import Skeleton from "components/ui/Skeleton";
import { ModuleBadge } from "layouts/SidebarBadge";
import { getModuleIcon } from "utils/module-icon";
import { getRoleConfig } from "../role-config";
import apiRoleModuleMapping from "./api";
import apiRoleAndPermissions from "../api";
import {
  PERMISSIONS, buildIndex, groupState, isSamePermissions, setPermissions, toPayload, toPermissionState,
} from "./features/permissions";
import { buildOrderTree, flattenOrderTree, moveInGroup, movedIds, savedArrangement } from "./features/sidebar-order";
import SidebarOrderTab from "./role-module-access-modal/SidebarOrderTab";

const TABS = [
  { key: "access", label: "Access", icon: ShieldCheck },
  { key: "order", label: "Sidebar Order", icon: ListOrdered },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "unassigned", label: "Not assigned" },
  { key: "changed", label: "Unsaved" },
];

const GRID = "grid grid-cols-[52px_minmax(0,1fr)_repeat(5,84px)] items-center";

const PermissionSwitch = ({ state, onToggle, disabled, label, small = false }) => {
  const track = small ? "h-[18px] w-8" : "h-5 w-9";
  const knob = small ? "h-3 w-3" : "h-3.5 w-3.5";
  const position = {
    on: small ? "translate-x-[16px]" : "translate-x-[19px]",
    mixed: small ? "translate-x-[9px]" : "translate-x-[11px]",
    off: "translate-x-[2px]",
  }[state];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={state === "on"}
      aria-label={state === "mixed" ? `${label} (partly on)` : label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`relative inline-flex shrink-0 items-center rounded-full border transition-colors duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong focus-visible:ring-offset-1 focus-visible:ring-offset-surface
        disabled:cursor-not-allowed disabled:opacity-50 ${track}
        ${state === "on" ? "border-primary bg-primary" : state === "mixed" ? "border-primary bg-primary_tint" : "border-border_strong bg-surface_alt"}`}
    >
      <span
        className={`inline-block rounded-full shadow-sm transition-transform duration-200 ${knob} ${position}
          ${state === "mixed" ? "bg-primary" : state === "on" ? "bg-white" : "bg-text_muted"}`}
      />
    </button>
  );
};

const SkeletonRows = () => (
  <div className="divide-y divide-border">
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className={`${GRID} px-3 py-3`}>
        <div className="h-3 w-5 rounded-md bg-surface_alt animate-pulse" />
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-md bg-surface_alt animate-pulse" />
          <div className="space-y-1.5">
            <div className="h-3 rounded-md bg-surface_alt animate-pulse" style={{ width: `${110 + ((i * 37) % 90)}px` }} />
            <div className="h-2.5 w-28 rounded-md bg-surface_alt animate-pulse" />
          </div>
        </div>
        {PERMISSIONS.map((p) => (
          <div key={p.key} className="flex justify-center">
            <div className="h-5 w-9 rounded-full bg-surface_alt animate-pulse" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const ModuleRow = ({ module, number, isChild, hasChildren, isExpanded, onExpand, childSummary, states, onToggle, disabled, isChanged, isAssigned }) => {
  const Icon = getModuleIcon(module.icon);

  return (
    <div
      className={`${GRID} group border-b border-border px-3 py-2 hover:bg-surface_alt
        ${hasChildren ? "cursor-pointer" : ""} ${isChild ? "bg-surface" : ""}`}
      onClick={hasChildren ? onExpand : undefined}
    >
      <div className="flex items-center gap-1.5 text-[11px] tabular-nums text-text_muted">
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${isChanged ? "bg-warning" : "bg-transparent"}`}
          title={isChanged ? "Unsaved change" : undefined}
        />
        {number}
      </div>

      <div className={`flex min-w-0 items-center gap-2 ${isChild ? "relative pl-7" : ""}`}>
        {isChild && <span className="absolute -bottom-2 -top-2 left-[9px] w-px bg-border" aria-hidden="true" />}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            aria-label={isExpanded ? `Collapse ${module.name}` : `Expand ${module.name}`}
            aria-expanded={isExpanded}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-text_muted hover:bg-border hover:text-text_primary"
          >
            <ChevronRight size={14} className={`transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
          </button>
        ) : (
          !isChild && <span className="w-5 shrink-0" />
        )}

        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors
            ${isAssigned ? "border-primary bg-primary_tint text-primary" : "border-border bg-surface_alt text-text_muted"}`}
        >
          <Icon size={14} />
        </div>

        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={`truncate text-[13px] font-medium ${isAssigned ? "text-text_primary" : "text-text_secondary"}`}>
              {module.name}
            </span>
            {module.badge && <ModuleBadge badge={module.badge} />}
            {!module.is_activated && (
              <span
                className="shrink-0 rounded-sm border border-border px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-text_muted"
                title="Hidden from every sidebar until it is activated in Module Master"
              >
                Inactive
              </span>
            )}
            {hasChildren && (
              <span className="shrink-0 rounded-full border border-border bg-surface_alt px-1.5 py-px text-[10px] font-medium tabular-nums text-text_secondary">
                {childSummary.assigned}/{childSummary.total}
              </span>
            )}
          </div>
          {module.path && module.path !== "#" && (
            <div className="truncate font-mono text-[11px] text-text_muted" title={module.path}>
              {module.path}
            </div>
          )}
        </div>
      </div>

      {PERMISSIONS.map((p) => (
        <div key={p.key} className="flex justify-center">
          <PermissionSwitch
            state={states[p.key]}
            onToggle={() => onToggle(p.key, states[p.key])}
            disabled={disabled}
            label={`${p.label} access for ${module.name}${hasChildren ? " and its sub-modules" : ""}`}
          />
        </div>
      ))}
    </div>
  );
};

const RoleModuleAccessModal = ({ isOpen, role, onClose, onSaved }) => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [modules, setModules] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [initial, setInitial] = useState({});
  const [perms, setPerms] = useState({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [otherRoles, setOtherRoles] = useState([]);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [tab, setTab] = useState("access");
  // Sidebar order per group as saved, and as the user has arranged it (see features/sidebar-order.js).
  const [initialArrangement, setInitialArrangement] = useState({});
  const [arrangement, setArrangement] = useState({});
  const copyMenuRef = useRef(null);

  const roleId = role?.id;

  const load = useCallback(async () => {
    if (!roleId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [res, rolesRes] = await Promise.all([
        apiRoleModuleMapping.getByRole(roleId),
        apiRoleAndPermissions.getRoles().catch(() => null),
      ]);
      if (!res.data.status) {
        setLoadError(res.data.message || "Could not load modules for this role.");
        return;
      }
      const { modules: list, is_editable } = res.data.data;
      const state = toPermissionState(list);
      const index = buildIndex(list);
      setModules(list);
      setIsEditable(Boolean(is_editable));
      setInitial(state);
      setPerms(state);
      const savedOrder = savedArrangement(index, state);
      setInitialArrangement(savedOrder);
      setArrangement(savedOrder);
      setExpanded(new Set(index.roots.filter((id) => index.childrenById[id].some((cid) => state[cid].can_view))));
      setOtherRoles((rolesRes?.data?.data || []).filter((r) => r.id !== roleId));
    } catch (error) {
      setLoadError(error.response?.data?.message || "Could not load modules for this role.");
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setFilter("all");
    setTab("access");
    setConfirmDiscard(false);
    setCopyMenuOpen(false);
    load();
  }, [isOpen, load]);

  const index = useMemo(() => buildIndex(modules), [modules]);

  const changedSet = useMemo(
    () => new Set(modules.filter((m) => !isSamePermissions(perms[m.id], initial[m.id])).map((m) => m.id)),
    [modules, perms, initial]
  );
  const accessDirty = changedSet.size > 0;

  // The assigned modules as this role's sidebar will show them, and as it would without the user's moves.
  const orderTree = useMemo(() => buildOrderTree(index, perms, arrangement), [index, perms, arrangement]);
  const baseOrderTree = useMemo(
    () => buildOrderTree(index, perms, initialArrangement),
    [index, perms, initialArrangement]
  );
  const movedSet = useMemo(() => movedIds(orderTree, baseOrderTree), [orderTree, baseOrderTree]);
  const orderDirty = movedSet.size > 0;
  const isDirty = accessDirty || orderDirty;
  const assignedCount = useMemo(() => modules.filter((m) => perms[m.id]?.can_view).length, [modules, perms]);

  const query = search.trim().toLowerCase();

  const visibleTree = useMemo(() => {
    const matchesQuery = (id) => {
      if (!query) return true;
      const m = index.byId[id];
      return m.name.toLowerCase().includes(query) || (m.path || "").toLowerCase().includes(query);
    };
    const matchesFilter = (id) => {
      if (filter === "assigned") return Boolean(perms[id]?.can_view);
      if (filter === "unassigned") return !perms[id]?.can_view;
      if (filter === "changed") return changedSet.has(id);
      return true;
    };

    return index.roots.reduce((acc, rootId) => {
      const rootMatchesQuery = matchesQuery(rootId);
      const children = index.childrenById[rootId].filter(
        (cid) => matchesFilter(cid) && (rootMatchesQuery || matchesQuery(cid))
      );
      if ((rootMatchesQuery && matchesFilter(rootId)) || children.length) {
        acc.push({ id: rootId, children });
      }
      return acc;
    }, []);
  }, [index, perms, query, filter, changedSet]);

  const listedIds = useMemo(() => visibleTree.flatMap((node) => [node.id, ...node.children]), [visibleTree]);
  const forceExpand = Boolean(query) || filter !== "all";
  const expandableRoots = index.roots.filter((id) => index.childrenById[id].length);
  const allExpanded = expandableRoots.length > 0 && expandableRoots.every((id) => expanded.has(id));

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleExpandAll = () => setExpanded(allExpanded ? new Set() : new Set(expandableRoots));

  const handleToggle = (ids, key, currentState) => {
    if (!isEditable || !ids.length) return;
    setPerms((prev) => setPermissions(prev, ids, key, currentState !== "on", index));
  };

  const handleMove = (key, ids, fromIndex, toIndex) => {
    if (!isEditable || fromIndex === toIndex || toIndex < 0 || toIndex >= ids.length) return;
    setArrangement((prev) => moveInGroup(prev, key, ids, fromIndex, toIndex));
  };

  const handleReset = () => {
    setPerms(initial);
    setArrangement(initialArrangement);
  };

  const requestClose = useCallback(() => {
    if (saving) return;
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  }, [saving, isDirty, onClose]);

  const handleSave = useCallback(async () => {
    if (!isEditable || !isDirty || saving) return;
    setSaving(true);
    try {
      // Access first, so modules assigned in this edit exist before their order is saved.
      if (accessDirty) {
        const res = await apiRoleModuleMapping.save(roleId, toPayload(perms));
        if (!res.data.status) {
          showAlert({ type: "warning", message: res.data.message });
          return;
        }
        // Saved: if the order save below fails, a retry only sends the order.
        setInitial(perms);
        onSaved?.(res.data.data);
      }
      if (orderDirty) {
        const res = await apiRoleModuleMapping.saveOrder(roleId, flattenOrderTree(orderTree));
        if (!res.data.status) {
          showAlert({ type: "warning", message: res.data.message });
          return;
        }
      }
      showAlert({
        type: "success",
        message:
          accessDirty && orderDirty
            ? "Module access and sidebar order updated successfully"
            : accessDirty
            ? "Module access updated successfully"
            : "Sidebar order updated successfully",
      });
      onClose();
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Could not save your changes. Please try again." });
    } finally {
      setSaving(false);
    }
  }, [isEditable, isDirty, saving, accessDirty, orderDirty, roleId, perms, orderTree, showAlert, onSaved, onClose]);

  const copyFromRole = async (source) => {
    setCopyMenuOpen(false);
    setCopying(true);
    try {
      const res = await apiRoleModuleMapping.getByRole(source.id);
      if (!res.data.status) {
        showAlert({ type: "warning", message: res.data.message });
        return;
      }
      const copied = toPermissionState(modules, res.data.data.modules);
      const copiedCount = Object.values(copied).filter((p) => p.can_view).length;
      setPerms(copied);
      showAlert({
        type: "info",
        message: `Copied ${copiedCount} module ${copiedCount === 1 ? "assignment" : "assignments"} from ${getRoleConfig(source.role).label}. Review them, then save to apply.`,
      });
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Could not copy access from that role." });
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (confirmDiscard) setConfirmDiscard(false);
        else if (copyMenuOpen) setCopyMenuOpen(false);
        else requestClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, confirmDiscard, copyMenuOpen, requestClose, handleSave]);

  useEffect(() => {
    if (!copyMenuOpen) return undefined;
    const onPointer = (e) => {
      if (!copyMenuRef.current?.contains(e.target)) setCopyMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [copyMenuOpen]);

  if (!isOpen || !role) return null;

  const config = getRoleConfig(role.role);
  const RoleIcon = config.icon;
  const userCount = role._count?.user ?? 0;
  const progress = modules.length ? Math.round((assignedCount / modules.length) * 100) : 0;
  const isOwnRole = user?.user_role?.id === roleId;
  const filterCounts = {
    all: modules.length,
    assigned: assignedCount,
    unassigned: modules.length - assignedCount,
    changed: changedSet.size,
  };
  const controlsDisabled = !isEditable || saving || copying;
  const unsavedParts = [
    accessDirty && `${changedSet.size} unsaved access ${changedSet.size === 1 ? "change" : "changes"}`,
    orderDirty && "Sidebar order not saved",
  ].filter(Boolean);
  const changedModulesText = `${changedSet.size} ${changedSet.size === 1 ? "module" : "modules"}`;

  const renderOrderBody = () => {
    if (loading) return <div className="p-3"><Skeleton.Rows rows={6} /></div>;
    if (loadError) return renderBody();
    return (
      <SidebarOrderTab
        tree={orderTree}
        byId={index.byId}
        moved={movedSet}
        onMove={handleMove}
        editable={isEditable}
        disabled={saving}
      />
    );
  };

  const renderBody = () => {
    if (loading) return <SkeletonRows />;

    if (loadError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-danger_border bg-danger_bg text-danger">
            <AlertTriangle size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-text_primary">Modules could not be loaded</p>
            <p className="mt-1 text-xs text-text_secondary">{loadError}</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-text_primary hover:bg-surface_alt"
          >
            <RefreshCw size={13} /> Try again
          </button>
        </div>
      );
    }

    if (!modules.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-16 text-center">
          <PackageOpen size={28} className="text-text_muted" />
          <p className="text-sm font-semibold text-text_primary">No modules yet</p>
          <p className="max-w-sm text-xs text-text_secondary">
            Create modules in the Module Master tab first, then come back here to assign them to this role.
          </p>
        </div>
      );
    }

    if (!visibleTree.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-16 text-center">
          <SearchX size={28} className="text-text_muted" />
          <p className="text-sm font-semibold text-text_primary">No modules match</p>
          <p className="text-xs text-text_secondary">Try a different search term or filter.</p>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setFilter("all");
            }}
            className="mt-1 text-xs font-medium text-primary hover:underline"
          >
            Clear search and filters
          </button>
        </div>
      );
    }

    return visibleTree.map((node, i) => {
      const children = index.childrenById[node.id];
      const groupIds = [node.id, ...children];
      const hasChildren = children.length > 0;
      const isExpanded = hasChildren && (forceExpand || expanded.has(node.id));
      const rowStates = Object.fromEntries(PERMISSIONS.map((p) => [p.key, groupState(perms, groupIds, p.key)]));

      return (
        <React.Fragment key={node.id}>
          <ModuleRow
            module={index.byId[node.id]}
            number={i + 1}
            hasChildren={hasChildren}
            isExpanded={isExpanded}
            onExpand={() => toggleExpand(node.id)}
            childSummary={{ assigned: children.filter((cid) => perms[cid]?.can_view).length, total: children.length }}
            states={rowStates}
            onToggle={(key, state) => handleToggle(groupIds, key, state)}
            disabled={controlsDisabled}
            isChanged={groupIds.some((id) => changedSet.has(id))}
            isAssigned={Boolean(perms[node.id]?.can_view)}
          />
          {isExpanded &&
            node.children.map((childId, j) => (
              <ModuleRow
                key={childId}
                module={index.byId[childId]}
                number={`${i + 1}.${j + 1}`}
                isChild
                states={Object.fromEntries(PERMISSIONS.map((p) => [p.key, perms[childId]?.[p.key] ? "on" : "off"]))}
                onToggle={(key, state) => handleToggle([childId], key, state)}
                disabled={controlsDisabled}
                isChanged={changedSet.has(childId)}
                isAssigned={Boolean(perms[childId]?.can_view)}
              />
            ))}
        </React.Fragment>
      );
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1999] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-module-access-title"
    >
      <div className="rma-fade absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={requestClose} />

      <div className="rma-in relative flex h-[min(90vh,880px)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-surface text-text_primary shadow-[0_24px_70px_rgba(0,0,0,0.28)]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3.5">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${config.className}`}>
              <RoleIcon size={20} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="role-module-access-title" className="text-base font-semibold">
                  Module Access
                </h2>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${config.className}`}>
                  <RoleIcon size={11} />
                  {config.label}
                </span>
                {!loading && !loadError && !isEditable && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface_alt px-2 py-0.5 text-[11px] font-medium text-text_secondary">
                    <Lock size={11} /> View only
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-text_secondary">
                Choose which modules {config.label} users can open, and what they can do inside each one.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-text_muted hover:bg-surface_alt hover:text-text_primary"
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary */}
        <div className="grid items-center gap-3 border-b border-border bg-surface_alt px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-8 sm:px-6">
          <div>
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <span className="text-text_secondary">
                <span className="text-sm font-semibold tabular-nums text-text_primary">{assignedCount}</span> of{" "}
                <span className="tabular-nums">{modules.length}</span> modules assigned
              </span>
              <span className="tabular-nums text-text_muted">{progress}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-border">
              <div className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-text_secondary">
            <Users size={14} className="text-text_muted" />
            <span>
              Applies to <span className="font-semibold tabular-nums text-text_primary">{userCount.toLocaleString()}</span>{" "}
              {userCount === 1 ? "user" : "users"} with this role
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border px-4 sm:px-6" role="tablist" aria-label="Module access sections">
          {TABS.map((t) => {
            const TabIcon = t.icon;
            const isActive = tab === t.key;
            const hasUnsaved = t.key === "access" ? accessDirty : orderDirty;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.key)}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium
                  ${isActive ? "border-primary text-text_primary" : "border-transparent text-text_secondary hover:text-text_primary"}`}
              >
                <TabIcon size={14} />
                {t.label}
                {hasUnsaved && <span className="h-1.5 w-1.5 rounded-full bg-warning" title="Unsaved changes" />}
              </button>
            );
          })}
        </div>

        {/* Notices */}
        {!loading && !loadError && (isOwnRole || !isEditable) && (
          <div className="px-4 pt-3 sm:px-6">
            {isEditable && isOwnRole ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-warning_border bg-warning_bg px-3 py-2 text-xs text-text_primary">
                <AlertTriangle size={15} className="mt-px shrink-0 text-warning" />
                <span>
                  This is your own role. If you remove access to the module that holds this page, you will lose access to it after your next refresh.
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded-lg border border-info_border bg-info_bg px-3 py-2 text-xs text-text_primary">
                <Info size={15} className="mt-px shrink-0 text-info" />
                <span>You can review this role's access, but only a Super Admin can change it.</span>
              </div>
            )}
          </div>
        )}

        {tab === "order" && (
          <div className="flex items-start gap-2 px-4 py-3 text-xs text-text_secondary sm:px-6">
            <Info size={14} className="mt-px shrink-0 text-text_muted" />
            <span>
              {isEditable
                ? `Drag a module, or use its arrows, to set where it shows in the ${config.label} sidebar. Sub-modules move within their parent.`
                : `The order modules show in the ${config.label} sidebar.`}
            </span>
          </div>
        )}

        {/* Toolbar */}
        {tab === "access" && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
            <div className="relative w-full sm:w-72">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by module name or path"
                disabled={loading || Boolean(loadError)}
                className="h-8 w-full rounded-md border border-border bg-surface pl-9 pr-8 text-[13px] text-text_primary placeholder:text-text_muted focus:border-border_strong focus:outline-none disabled:opacity-60"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-text_muted hover:text-text_primary"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="flex max-w-full overflow-x-auto rounded-md border border-border bg-surface_alt p-0.5" role="tablist" aria-label="Filter modules">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  disabled={loading || Boolean(loadError)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium
                    ${filter === f.key ? "bg-surface text-text_primary shadow-sm" : "text-text_secondary hover:text-text_primary"}`}
                >
                  {f.label}
                  <span
                    className={`rounded-full px-1.5 text-[10px] tabular-nums
                      ${f.key === "changed" && filterCounts.changed ? "bg-warning_bg text-warning" : "bg-border text-text_secondary"}`}
                  >
                    {filterCounts[f.key]}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:ml-auto">
              {isEditable && otherRoles.length > 0 && (
                <div className="relative" ref={copyMenuRef}>
                  <button
                    type="button"
                    onClick={() => setCopyMenuOpen((v) => !v)}
                    disabled={controlsDisabled || loading}
                    aria-haspopup="menu"
                    aria-expanded={copyMenuOpen}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-3 text-xs font-medium text-text_primary hover:bg-surface_alt disabled:opacity-50"
                  >
                    {copying ? <RefreshCw size={13} className="animate-spin" /> : <Copy size={13} />}
                    Copy from role
                    <ChevronDown size={13} className="text-text_muted" />
                  </button>
                  {copyMenuOpen && (
                    <div
                      role="menu"
                      className="rma-in absolute right-0 top-full z-30 mt-1.5 w-60 overflow-hidden rounded-xl border border-border bg-surface p-1 shadow-xl"
                    >
                      <p className="px-2.5 pb-1.5 pt-1 text-[11px] text-text_muted">
                        Replaces the current selection. Nothing is saved until you click Save.
                      </p>
                      {otherRoles.map((r) => {
                        const rc = getRoleConfig(r.role);
                        const RcIcon = rc.icon;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            role="menuitem"
                            onClick={() => copyFromRole(r)}
                            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium text-text_primary hover:bg-surface_alt"
                          >
                            <span className={`flex h-6 w-6 items-center justify-center rounded-md border ${rc.className}`}>
                              <RcIcon size={12} />
                            </span>
                            {rc.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={toggleExpandAll}
                disabled={loading || !expandableRoots.length || forceExpand}
                title={forceExpand ? "Groups stay expanded while searching or filtering" : undefined}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-3 text-xs font-medium text-text_primary hover:bg-surface_alt disabled:opacity-50"
              >
                {allExpanded ? <ChevronsDownUp size={13} /> : <ChevronsUpDown size={13} />}
                {allExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>
          </div>
        )}

        {/* Permission table / sidebar order list */}
        <div className="min-h-0 flex-1 px-4 pb-3 sm:px-6">
          <div className="h-full overflow-auto rounded-lg border border-border">
            {tab === "order" ? (
              <div className="min-w-[520px]">{renderOrderBody()}</div>
            ) : (
              <div className="min-w-[760px]">
                <div className={`${GRID} sticky top-0 z-10 border-b border-border bg-surface_alt px-3 py-2.5`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-text_secondary">#</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-text_secondary">Module</div>
                  {PERMISSIONS.map((p) => (
                    <div key={p.key} className="flex flex-col items-center gap-1.5">
                      <Tooltip content={p.hint}>
                        <span className="cursor-help text-[11px] font-semibold uppercase tracking-wide text-text_secondary">
                          {p.label}
                        </span>
                      </Tooltip>
                      <PermissionSwitch
                        small
                        state={loading ? "off" : groupState(perms, listedIds, p.key)}
                        onToggle={() => handleToggle(listedIds, p.key, groupState(perms, listedIds, p.key))}
                        disabled={controlsDisabled || loading || !listedIds.length}
                        label={`${p.label} access for every listed module`}
                      />
                    </div>
                  ))}
                </div>
                {renderBody()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface_alt px-4 py-3 sm:px-6">
          <div className="min-w-0 text-xs text-text_secondary">
            {isDirty ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-warning">
                <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                {unsavedParts.join(" · ")}
              </span>
            ) : (
              <span>No unsaved changes</span>
            )}
            <span className="hidden text-text_muted md:inline"> &middot; Users see updates the next time they refresh or sign in.</span>
          </div>
          <div className="flex items-center gap-2">
            {isEditable && isDirty && (
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-text_secondary hover:bg-surface hover:text-text_primary disabled:opacity-50"
              >
                <RotateCcw size={13} /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text_primary hover:bg-background disabled:opacity-50"
            >
              {isEditable ? "Cancel" : "Close"}
            </button>
            {isEditable && (
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saving}
                title="Save changes (Ctrl+S / Cmd+S)"
                className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary_hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </div>

        {/* Discard confirmation */}
        {confirmDiscard && (
          <div className="rma-fade absolute inset-0 z-40 flex items-center justify-center bg-black/35 p-4">
            <div className="rma-in w-full max-w-sm rounded-xl border border-border bg-surface p-5 shadow-2xl" role="alertdialog" aria-labelledby="rma-discard-title">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-warning_border bg-warning_bg text-warning">
                <AlertTriangle size={19} />
              </div>
              <h3 id="rma-discard-title" className="text-sm font-semibold text-text_primary">
                Discard unsaved changes?
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-text_secondary">
                {accessDirty && orderDirty
                  ? `You changed access for ${changedModulesText} and the sidebar order.`
                  : accessDirty
                  ? `You changed access for ${changedModulesText}.`
                  : "You changed the sidebar order."}{" "}
                If you close now, these changes will be lost.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  autoFocus
                  onClick={() => setConfirmDiscard(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-text_primary hover:bg-surface_alt"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDiscard(false);
                    onClose();
                  }}
                  className="rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                >
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes rma-in {
          from { opacity: 0; transform: scale(0.97) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes rma-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .rma-in { animation: rma-in 0.2s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .rma-fade { animation: rma-fade 0.15s ease-out both; }
      `}</style>
    </div>,
    document.body
  );
};

export default RoleModuleAccessModal;
